import React from 'react'
import PropTypes from 'prop-types'
import { REQUEST_STATUSES } from './requestContract'
import classes from './RequestFilters.module.css'

/**
 * Two axes, because the service only supports two questions today: where a
 * request got to, and which artist it was for. Everything coming through this
 * endpoint is an artist sync started by a person, so the old type and source
 * axes have nothing to sort on.
 *
 * Filtered in the browser. `GET /ingest/requests` takes no query at all and
 * returns the most recent 100.
 */

export const STATUS_FILTERS = Object.freeze([
  { id: 'all', label: 'All' },
  { id: 'in_flight', label: 'Still going' },
  ...Object.entries(REQUEST_STATUSES).map(([id, meta]) => ({ id, label: meta.label })),
])

export const FILTER_DEFAULTS = Object.freeze({ status: 'all', q: '' })

const IN_FLIGHT = new Set(['queued', 'running'])

function matchesText(row, needle) {
  if (!needle) return true
  // list() now also carries the committed Feels name and the staged YouTube
  // name (store.js's artist_display_name / youtube_name), so a name search
  // matches too - not just the ids this used to be limited to.
  const hay = [row.artist_display_name, row.youtube_name, row.artist_f_id, row.source_id, row.requested_by, row.id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return hay.includes(needle.toLowerCase())
}

/** Pure, so every combination is testable without a DOM. */
export function applyRequestFilters(rows, { status, q }) {
  return (rows || []).filter(row => {
    if (!row) return false
    if (q && !matchesText(row, q)) return false
    if (status === 'in_flight') return IN_FLIGHT.has(row.status)
    if (status && status !== 'all' && row.status !== status) return false
    return true
  })
}

export default function RequestFilters({ status, q, onChange, counts }) {
  const isFiltered = status !== FILTER_DEFAULTS.status || Boolean(q)

  return (
    <div className={classes.wrapper}>
      <div className={classes.searchBox}>
        <span className={classes.searchIcon} aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="4.6" stroke="currentColor" strokeWidth="1.6" />
            <path d="M10.6 10.6L14 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </span>
        <input
          className={classes.search}
          type="text"
          value={q || ''}
          placeholder="Search by name, channel, artist id or who asked…"
          aria-label="Search requests"
          onChange={e => onChange({ q: e.target.value })}
        />
        {q && (
          <button
            type="button"
            className={classes.searchClear}
            onClick={() => onChange({ q: '' })}
            aria-label="Clear search"
          >
            Clear
          </button>
        )}
      </div>

      <label
        className={status !== FILTER_DEFAULTS.status ? `${classes.filter} ${classes.filterOn}` : classes.filter}
        htmlFor="ask-filter-status"
      >
        <span className={classes.filterLabel}>Status</span>
        <select
          id="ask-filter-status"
          className={classes.filterSelect}
          value={status}
          onChange={e => onChange({ status: e.target.value })}
        >
          {STATUS_FILTERS.map(o => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      {isFiltered && (
        <button type="button" className={classes.clearButton} onClick={() => onChange({ ...FILTER_DEFAULTS })}>
          Clear filters
        </button>
      )}

      {counts && (
        <span className={classes.countLine}>
          {counts.shown === counts.total
            ? `${counts.total} ${counts.total === 1 ? 'request' : 'requests'}`
            : `${counts.shown} of ${counts.total}`}
        </span>
      )}
    </div>
  )
}

RequestFilters.propTypes = {
  status: PropTypes.string.isRequired,
  q: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  counts: PropTypes.shape({ shown: PropTypes.number.isRequired, total: PropTypes.number.isRequired }),
}
